import { FULL_HD_CONSTRAINTS } from '@/utils/constants';
import { sleepAsync } from '@/utils/functions';
import { NumberFilter } from 'ag-grid-community';
import EventEmitter from 'eventemitter3';

declare var JitsiMeetJS: any;

var isJitsiLibInitialized = false;

export var JitsiLib: any = {};

export function initMediaServerUrl(url: string): Promise<any> {
  if (isJitsiLibInitialized) {
    return Promise.resolve();
  }
  isJitsiLibInitialized = true;
  return new Promise((resolv, reject) => {
    let script = document.createElement('script');
    script.onload = () => {
      JitsiLib = JitsiMeetJS;
      JitsiMeetJS.init({ enableAnalyticsLogging: true });
      isJitsiLibInitialized = true;
      resolv(JitsiMeetJS);
    };
    script.onerror = (error) => {
      isJitsiLibInitialized = false;
      reject(error);
    };
    script.async = true;
    script.src = `${url}/libs/lib-jitsi-meet.min.js`;
    document.head.appendChild(script);
  });
}
export interface JitsiConnectionConfig {
  hosts: {
    domain: string;
    muc: string;
  };
  serviceUrl: string;
  clientNode: string;
  enableLipSync?: boolean;
  xmppPing?: { interval?: number; timeout?: NumberFilter; threshold: number };
  websocketKeepAlive?: number;
  websocketKeepAliveUrl?: string;
  // modules_enabled: Array<string>
}
export interface JitsiConnection {
  connect(option?: { id: string; password: string }): void;
  disconnect(): Promise<any>;
  initJitsiConference(name: string, option: any): JitsiConference;
  addEventListener(event: string, handler: any): void;
  removeEventListener(event: string, handler: any): void;
  addFeature(feature: string, submit: boolean): void;
  removeFeature(feature: string, submit: boolean): void;
}
export interface JitsiConference {
  isJoined(): boolean;
  join(pass: string | null): void;
  leave(): Promise<any>;
  myUserId(): string;
  /**
   *  - Returns array with JitsiTrack objects for the local streams.
   */
  getLocalTracks(): JitsiTrack[];
  /**
   *  - Subscribes the passed listener to the event.
   * @param event - one of the events from JitsiMeetJS.events.conference object.
   * @param listener - handler for the event.
   */
  addEventListener(event: string, listener: any): void;

  /**
   *  - Removes event listener.
   * @param event - one of the events from JitsiMeetJS.events.conference object.
   * @param listener - the listener that will be removed.
   */
  removeEventListener(event: string, listener: any): void;
  /**
   *  - alias for addEventListener
   * @param event - one of the events from JitsiMeetJS.events.conference object.
   * @param listener - handler for the event.
   */
  on(event: string, listener: any): void;

  /**
   *  - alias for removeEventListener
   * @param event - one of the events from JitsiMeetJS.events.conference object.
   * @param listener - the listener that will be removed.
   */
  off(event: string, listener: any): void;

  /**
   *  - sends the given string to other participants in the conference.
   * @param text
   */
  sendTextMessage(text: string): void;

  /**
   *  - changes the display name of the local participant.
   * @param name - the new display name
   */
  setDisplayName(name: string): void;

  /**
   *  - Elects the participant with the given id to be the selected participant in order to receive higher video quality (if simulcast is enabled).
   * @param participantId - the identifier of the participant
   * @throws Throws NetworkError or InvalidStateError or Error if the operation fails.
   */
  selectParticipant(participantId: string): void;
  /**
   *  - sends user defined system command to the other participants
   *
   * NOTE: When you use that method the passed object will be added in every system message that is sent to the other participants. It might be sent more than once.
   *
   * @param name - the name of the command.
   * @param values - JS object. The object has the following structure:
   *             {
   *                 value: the_value_of_the_command,
   *                 attributes: {}, // map with keys the name of the attribute and values - the values of the attributes.
   *                 children: [] // array with JS object with the same structure.
   *             }
   */
  sendCommand(name: string, values: { value: string; attributes: any; children: any[] }): void;

  /**
   *  - Sends only one time a user defined system command to the other participants
   * @param name
   * @param values
   */
  sendCommandOnce(name: string, values: any): void;
  /**
   *  - removes a command for the list of the commands that are sent to the ther participants
   * @param name - the name of the command
   */
  removeCommand(name: string): void;
  /**
   *  - adds listener
   * @param command - string for the name of the command
   * @param handler (values) - the listener that will be called when a command is received from another participant.
   */
  addCommandListener(command: string, handler: any): void;

  /**
   *  - removes the listeners for the specified command
   * @param command - the name of the command
   */
  removeCommandListener(command: string): void;
  /**
   *  - Adds JitsiLocalTrack object to the conference. Throws an error if adding second video stream. Returns Promise.
   * @param track - the JitsiLocalTrack
   */
  addTrack(track: JitsiTrack): Promise<JitsiTrack>;

  /**
   *  - Removes JitsiLocalTrack object to the conference. Returns Promise.
   * @param track - the JitsiLocalTrack
   */
  removeTrack(track: JitsiTrack): Promise<JitsiTrack>;

  /**
   *  - Check if at least one user supports DTMF.
   */
  isDTMFSupported(): boolean;
  /**
   *  - returns string with the local user role ("moderator" or "none")
   */
  getRole(): 'moderator' | 'none';

  /**
   *  - checks if local user has "moderator" role
   */
  isModerator(): boolean;
  /**
   *  - set password for the conference; returns Promise
   * Note: available only for moderator
   * @param password - string password
   */
  lock(password: string): Promise<any>;
  /**
   *  - unset conference password; returns Promise
   * Note: available only for moderator
   */
  unlock(): Promise<any>;
  /**
   *  - Kick participant from the conference
   * @param id - string participant id
   * @param reason - (optional) string, default 'You have been kicked.' - reason of the participant to kick
   */
  kickParticipant(id: string, reason?: string): void;
  /**
   *  - make all new participants join with muted audio/video
   * Note: available only for moderator
   * @param policy - JS object with following properties
   * audio - boolean if audio stream should be muted
   * video - boolean if video stream should be muted
   */
  setStartMutedPolicy(policy: any): void;

  /**
   *  - returns the current policy with JS object:
   * policy - JS object with following properties
   * audio - boolean if audio stream should be muted
   * video - boolean if video stream should be muted
   */
  getStartMutedPolicy(): any;
  /**
   *  - check if audio is muted on join
   */
  isStartAudioMuted(): boolean;

  /**
   *  - check if video is muted on join
   */
  isStartVideoMuted(): boolean;
  /**
   *  - change subject of the conference
   * Note: available only for moderator
   * @param subject - string new subject
   */
  setSubject(subject: string): void;

  /**
   *  - Sends message via the data channels.
   *
   * @param to - the id of the endpoint that should receive the message. If "" the message will be sent to all participants.
   * @param payload - JSON object - the payload of the message.
   * @throws Throws NetworkError or InvalidStateError or Error if the operation fails.
   */
  sendEndpointMessage(to: string, payload: any): void;
  /**
   *  - Sends a EndpointStats Colibri message on the bridge channel. This should be used instead of broadcastEndpointMessage for relaying local stats to all the remote endpoints.
   * @param payload - JSON object - the payload of the message.
   * @throws Throws NetworkError, InvalidStateError or Error if the operation fails.
   */
  sendEndpointStatsMessage(payload: any): void;
  /**
   *  - Sends broadcast message via the datachannels.
   * @param payload - JSON object - the payload of the message.
   * @throws Throws NetworkError or InvalidStateError or Error if the operation fails.
   */
  broadcastEndpointMessage(payload: any): void;
  /**
   *  - replaces the track currently being used as the sender's source with a new MediaStreamTrack. The new track must be of the same media kind (audio, video, etc) and switching the track should not require negotiation. replaceTrack(oldTrack, newTrack)
   * @param oldTrack
   * @param newTrack
   * @throws Throws NetworkError or InvalidStateError or Error if the operation fails.
   */
  replaceTrack(oldTrack: JitsiTrack, newTrack: JitsiTrack): void;
  /**
   *
   */
  // setReceiverConstraints() - set the constraints for the video that is requested from the bridge. This single message should be used in lieu of setLastN, setReceiverVideoConstraint and selectParticipants methods. These constraints are applicable to bridge connection only. More information about the signaling message format and how the Jitsi Videobridge allocates bandwidth can be found here.

  // videoConstraints - Object that specifies the constraints in the following format.
  // {
  //    'lastN': 20, // Number of videos requested from the bridge.
  //    'selectedEndpoints': ['A', 'B', 'C'], // The endpoints ids of the participants that are prioritized first.
  //    'onStageEndpoints': ['A'], // The endpoint ids of the participants that are prioritized up to a higher resolution.
  //    'defaultConstraints': { 'maxHeight': 180 }, // Default resolution requested for all endpoints.
  //    'constraints': { // Endpoint specific resolution.
  //        'A': { 'maxHeight': 720 }
  //    }
  // }
  /**
   * - set the desired resolution to get from JVB (180, 360, 720, 1080, etc). You should use that method if you are using simulcast.
   */
  setReceiverVideoConstraint(resolution: string): void;
  /**
   *  - set the desired resolution to send to JVB or the peer (180, 360, 720).
   * @param resolution
   */
  setSenderVideoConstraint(resolution: any): void;

  /**
   *  - checks if local user has joined as a "hidden" user. This is a specialized role used for integrations.
   */
  isHidden: boolean;
  /**
   *  - used to set a custom propery to the local participant("fullName": "Full Name", favoriteColor: "red", "userId": 234). Also this can be used to modify an already set custom property.
   * @param propertyKey - string - custom property name
   * @param propertyValue - string - custom property value
   */
  setLocalParticipantProperty(propertyKey: string, propertyValue: string): void;
  /**
   *  - Retrieves an array of all participants in this conference.
   */
  getParticipants(): JitsiParticipant[];
  /**
   *  - Revokes owner's rights to the participant. The particiapnt that invokes the function should have same or more rights than the targeted participant. This rights check is done at the XMPP server level.
   * @param participantId
   */
  revokeOwner(participantId: string): void;

  join(password?: string): void;
}
export type VideoType = 'camera' | 'desktop';

export interface JitsiAudioEffect {
  /**
   *  - Starts the effect and returns a new MediaStream that is to be swapped with the existing one.
   */
  startEffect(audioStream: MediaStream): void;
  /**
   *  - Stops the effect.
   */
  stopEffect(): void;
  /**
   *  - Checks if the local track supports the effect.
   */
  isEnabled(sourceLocalTrack: any): boolean;
}
export interface JitsiTrack {
  /**
   * returns string with the type of the track( "video" for the video tracks and "audio" for the audio tracks)
   */
  getType(): JitsiTrackType;
  /**
   * Returns the video type (camera or desktop) of this track.
   */
  videoType: VideoType;
  /**
   * ローカルかどうか。ドキュメントにないけどなぜかある関数。
   */
  isLocal(): boolean;
  /**
   *  - mutes the track. Returns Promise.
   *  Note: This method is implemented only for the local tracks.
   */
  mute(): Promise<any>;
  /**
   *  - unmutes the track. Returns Promise.
   *  Note: This method is implemented only for the local tracks.
   */
  unmute(): Promise<any>;
  /**
   *  - check if track is muted
   */
  isMuted(): boolean;
  /**
   *  - attaches the track to the given container.
   * @param container
   */
  attach(container: HTMLElement): void;
  /**
   * - removes the track from the container.
   * @param container
   */
  detach(container: HTMLElement): void;
  /**
   *  - disposes the track. If the track is added to a conference the track will be removed. Returns Promise.
   * Note: This method is implemented only for the local tracks.
   */
  dispose(): Promise<any>;
  disposed: boolean;
  /**
   *  - returns unique string for the track.
   */
  getId(): string;
  /**
   *  - returns id(string) of the track owner
   * Note: This method is implemented only for the remote tracks.
   */
  getParticipantId(): string;
  /**
   *  - sets new audio output device for track's DOM elements. Video tracks are ignored.
   * @param audioOutputDeviceId
   */
  setAudioOutput(audioOutputDeviceId: string): void;
  /**
   *  - returns device ID associated with track (for local tracks only)
   */
  getDeviceId(): string;
  /**
   *  - returns true if track is ended
   */
  isEnded(): boolean;
  /**
   *  - Applies the effect by swapping out the existing MediaStream on the JitsiTrack with the new
   * MediaStream which has the desired effect. "undefined" is passed to this function for removing the effect and for
   * restoring the original MediaStream on the JitsiTrack.
   * The following methods have to be defined for the effect instance.
   *
   * @param effect
   */
  setEffect(effect: JitsiAudioEffect | undefined): Promise<any>;
  track: MediaStreamTrack;
  addEventListener: (eventName: string | symbol, listener: (...args: any[]) => void) => JitsiTrack;
  removeEventListener: (eventName: string | symbol, listener: (...args: any[]) => void) => JitsiTrack;
}
export interface JitsiParticipant {
  /**
   * @returns {JitsiConference} The conference that this participant belongs
   * to.
   */
  getConference(): JitsiConference;
  /**
   * Gets the value of a property of this participant.
   */
  getProperty(name: string): string;
  /**
   * Checks whether this <tt>JitsiParticipant</tt> has any video tracks which
   * are muted according to their underlying WebRTC <tt>MediaStreamTrack</tt>
   * muted status.
   * @return {boolean} <tt>true</tt> if this <tt>participant</tt> contains any
   * video <tt>JitsiTrack</tt>s which are muted as defined in
   * {@link JitsiTrack.isWebRTCTrackMuted}.
   */
  hasAnyVideoTrackWebRTCMuted(): boolean;
  /**
   * Return participant's connectivity status.
   *
   * @returns {string} the connection status
   * <tt>ParticipantConnectionStatus</tt> of the user.
   * {@link ParticipantConnectionStatus}.
   */
  getConnectionStatus(): string;
  /**
   * Sets the value of a property of this participant, and fires an event if
   * the value has changed.
   * @name the name of the property.
   * @value the value to set.
   */
  setProperty(name: string, value: string): void;

  /**
   * @returns {Array.<JitsiTrack>} The list of media tracks for this
   * participant.
   */
  getTracks(): JitsiTrack[];

  /**
   * @param {MediaType} mediaType
   * @returns {Array.<JitsiTrack>} an array of media tracks for this
   * participant, for given media type.
   */
  getTracksByMediaType(mediaType: string): JitsiTrack[];

  /**
   * @returns {String} The ID of this participant.
   */
  getId(): string;

  /**
   * @returns {String} The JID of this participant.
   */
  getJid(): string;

  /**
   * @returns {String} The human-readable display name of this participant.
   */
  getDisplayName(): string;

  /**
   * @returns {String} The stats ID of this participant.
   */
  getStatsID(): string;

  /**
   * @returns {String} The status of the participant.
   */
  getStatus(): string;
  /**
   * @returns {Boolean} Whether this participant is a moderator or not.
   */
  isModerator(): boolean;
  /**
   * @returns {Boolean} Whether this participant is a hidden participant. Some
   * special system participants may want to join hidden (like for example the
   * recorder).
   */
  isHidden(): boolean;

  /**
   * @returns {Boolean} Wheter this participants replaces another participant
   * from the meeting.
   */
  isReplacing(): boolean;

  /**
   * @returns {Boolean} Wheter this participants will be replaced by another
   * participant in the meeting.
   */
  isReplaced(): boolean;
  /**
   * @returns {Boolean} Whether this participant has muted their audio.
   */
  isAudioMuted(): boolean;

  /**
   * @returns {Boolean} Whether this participant has muted their video.
   */
  isVideoMuted(): boolean;
  /**
   * @returns {String} The role of this participant.
   */
  getRole(): string;

  /**
   * Sets a new participant role.
   * @param {String} newRole - the new role.
   */
  setRole(newRole: string): void;
  /**
   * Sets whether participant is replacing another based on jwt.
   * @param {String} newIsReplacing - whether is replacing.
   */
  setIsReplacing(newIsReplacing: string): void;
  /**
   * Sets whether participant is being replaced by another based on jwt.
   * @param {boolean} newIsReplaced - whether is being replaced.
   */
  setIsReplaced(newIsReplaced: boolean): void;

  /**
   *
   */
  supportsDTMF(): any;

  /**
   * Returns a set with the features for the participant.
   * @returns {Promise<Set<String>, Error>}
   */
  getFeatures(): Promise<string>;

  /**
   * Checks current set features.
   * @param {String} feature - the feature to check.
   * @return {boolean} <tt>true</tt> if this <tt>participant</tt> contains the
   * <tt>feature</tt>.
   */
  hasFeature(feature: string): boolean;

  /**
   * Set new features.
   * @param {Set<String>|undefined} newFeatures - Sets new features.
   */
  setFeatures(newFeatures: Set<string>): void;

  /**
   * Returns the bot type for the participant.
   *
   * @returns {string|undefined} - The bot type of the participant.
   */
  getBotType(): string | undefined;

  /**
   * Sets the bot type for the participant.
   * @param {String} newBotType - The new bot type to set.
   */
  setBotType(newBotType: string): void;
}
export type UserParticipantEvents = 'tracksUpdate' | 'muteChanged' | 'audioLevelChanged' | 'updateParticipant';
export type JitsiTrackType = 'audio' | 'video';
export interface TrackAndElement {
  track: JitsiTrack;
  el: HTMLAudioElement | HTMLVideoElement;
}
export class UserParticipant {
  readonly tracks: TrackAndElement[] = [];
  private events_ = new EventEmitter<UserParticipantEvents>();
  private participantId_: string;
  private participant_: JitsiParticipant | null;

  get audioTracks(): TrackAndElement[] {
    return this.tracks.filter((t) => t.track.getType() === 'audio') ?? [];
  }
  get audioTrack(): TrackAndElement | null {
    const ts = this.audioTracks;
    if (ts.length === 0) {
      return null;
    }
    return ts[ts.length - 1];
  }
  get videoTrack(): TrackAndElement | null {
    return this.tracks.find((t) => t.track.getType() === 'video' && t.track.videoType !== 'desktop') ?? null;
  }
  get desktopTrack(): TrackAndElement | null {
    return this.tracks.find((t) => t.track.getType() === 'video' && t.track.videoType === 'desktop') ?? null;
  }
  get participantId(): string {
    return this.participantId_;
  }
  get participant(): JitsiParticipant | null {
    return this.participant_;
  }
  constructor(participantId: string, public readonly userId: string, participant: JitsiParticipant | null = null) {
    this.participant_ = participant;
    this.participantId_ = participantId;
  }
  on(
    eventName: UserParticipantEvents,
    handler: (trackOrAudioLevel: TrackAndElement | number | JitsiParticipant) => void,
  ) {
    this.events_.on(eventName, handler);
  }
  off(
    eventName: UserParticipantEvents,
    handler: (trackOrAudioLevel: TrackAndElement | number | JitsiParticipant) => void,
  ) {
    this.events_.off(eventName, handler);
  }
  emitEvent(eventName: UserParticipantEvents, trackOrAudioLevel?: TrackAndElement | number) {
    this.events_.emit(eventName, trackOrAudioLevel);
  }
  emitUpdateParticipant(participant: JitsiParticipant) {
    this.participant_ = participant;
    this.participantId_ = participant.getId();
    this.events_.emit('updateParticipant', participant);
  }
  emitUpdateParticipantId(id: string) {
    this.participantId_ = id;
    this.events_.emit('updateParticipant', null);
  }
}
export interface DeviceIds {
  micId: string;
  speakerId: string;
  cameraId: string;
}

export type JitsiControllerEvents =
  | 'tracksUpdate'
  | 'tracksRemoved'
  | 'desktopEnded'
  | 'disconnected'
  | 'joinedNewUser';
export class JitsiController {
  status: 'disconnected' | 'error' | 'connecting' | 'connected' | 'opened' = 'disconnected';
  connection: JitsiConnection | null = null;
  conference: JitsiConference | null = null;
  private readonly events_ = new EventEmitter<JitsiControllerEvents>();
  /** conference からイベント経由でもらった track 情報。conference.tracks と同じはず。 */
  readonly tracks: TrackAndElement[] = [];
  /** デバッグ用 通常は participantsMap/participantsUserMap を使ってください。*/
  participants: UserParticipant[] = [];
  readonly participantsMap: { [participantId: string]: UserParticipant } = {};
  readonly participantsUserMap: { [userId: string]: UserParticipant } = {};

  private localAudioTrack: JitsiTrack | null = null;
  private localVideoTrack: JitsiTrack | null = null;
  private localDesktopTracks: JitsiTrack[] | null = null;

  private isVideoOrDesktopTrackCreating = false;

  private micId: string = 'default';
  private speakerId: string = 'default';
  private cameraId: string = 'default';

  private isDestroyed: Promise<any> | null = null;
  private connectingPromise: Promise<JitsiController> | null = null;

  get conferenceId(): string {
    return this.confId;
  }
  get myParticipantId(): string {
    return this.conference?.myUserId() ?? '';
  }
  get hasAudioTrack(): boolean {
    return !!this.localAudioTrack;
  }
  get hasVideoTrack(): boolean {
    return !!this.localVideoTrack;
  }
  get hasDesktopTrack(): boolean {
    return this.isVideoOrDesktopTrackCreating || !!this.localDesktopTracks?.length;
  }
  get audioTrack() {
    return this.localAudioTrack;
  }
  get videoTrack() {
    return this.localVideoTrack;
  }
  get desktopTracks() {
    return this.localDesktopTracks;
  }
  /**
   * Jitsi の接続を管理するクラスのコンストラクタ。
   *
   * @param mediaServerUrl 接続先 jitsi server url
   * @param confId ルームのid
   * @param userId
   */
  constructor(
    private readonly mediaServerUrl: string,
    private readonly confId: string,
    private readonly userId: string,
  ) {
    // JitsiMeetJS.setLogLevel(JitsiMeetJS.logLevels.DEBUG);
    // JitsiMeetJS.setLogLevel(JitsiMeetJS.logLevels.INFO);
    JitsiMeetJS.setLogLevel(JitsiMeetJS.logLevels.ERROR);
  }
  setDevicesNames(micId: string, speakerId: string, cameraId: string) {
    if (this.micId !== micId) {
      this.micId = micId;
    }
    if (this.cameraId !== cameraId) {
      this.cameraId = cameraId;
    }
    if (this.speakerId !== speakerId) {
      this.speakerId = speakerId;
      this.tracks
        .filter((t) => t.track.getType() === 'audio' && !t.track.isLocal())
        .forEach((t) => t.track.setAudioOutput(speakerId));
    }
  }
  //#region device 関連
  validateDeviceIds(): Promise<DeviceIds> {
    return new Promise<DeviceIds>((resolv, reject) => {
      JitsiMeetJS.mediaDevices.enumerateDevices((devices: MediaDeviceInfo[]) => {
        let cameraId = this.validateDeviceId(devices, this.cameraId);
        let micId = this.validateDeviceId(devices, this.micId);
        let speakerId = this.validateDeviceId(devices, this.speakerId);
        resolv({ cameraId, micId, speakerId });
      });
    });
  }
  private validateDeviceId(availables: MediaDeviceInfo[], defaultId: string): string {
    const defaultDeviceId = 'default';

    if (!defaultId && availables.length) {
      let vd = availables.find((v) => v.deviceId == defaultDeviceId);
      if (vd) {
        defaultId = defaultDeviceId;
      } else {
        defaultId = availables[0].deviceId;
      }
    } else if (!defaultId || !availables.length) {
      defaultId = defaultDeviceId;
    } else {
      let vd = availables.find((v) => v.deviceId == defaultId);
      if (!vd) {
        vd = availables.find((v) => v.deviceId == defaultDeviceId);
        if (vd) {
          defaultId = defaultDeviceId;
        } else {
          defaultId = availables[0].deviceId;
        }
      }
    }
    return defaultId;
  }
  //#endregion
  destroy(): Promise<any> {
    if (!this.isDestroyed) {
      this.isDestroyed = this.disconnect();
    }
    return this.isDestroyed;
  }
  private async disconnect(): Promise<any> {
    if (this.status === 'disconnected') {
      return;
    }
    if (this.connectingPromise) {
      try {
        let promise = this.connectingPromise;
        this.connectingPromise = null;
        await promise;
      } catch (e) {
        // ignore
      }
    }
    await this.removeAudioTrack();
    await this.removeVideoTrack();
    await this.removeDesktopTrack();

    // const stime = Math.min(20 * 1000, 2000 + this.participants.length * 500);
    // await sleepAsync(stime);
    console.log('remove conference');

    if (this.conference) {
      if (this.conference.isJoined()) {
        try {
          await this.conference.leave();
        } catch (e) {
          // ignore
        }
      }
      this.removeJitsiEventListeners();
      this.conference = null;
    }
    console.log('remove connection');

    if (this.connection) {
      try {
        await this.connection.disconnect();
      } catch (e) {
        // ignore
      }
      this.connection = null;
    }
    this.status = 'disconnected';
  }
  on(eventName: JitsiControllerEvents, handler: (trackOrAudioLevel?: TrackAndElement | number) => void) {
    this.events_.on(eventName, handler);
  }
  off(eventName: JitsiControllerEvents, handler: (trackOrAudioLevel?: TrackAndElement | number) => void) {
    this.events_.off(eventName, handler);
  }
  onJoinUser(handler: (trackOrAudioLevel?: UserParticipant) => void) {
    this.events_.on('joinedNewUser', handler);
  }
  offJoinUser(handler: (trackOrAudioLevel?: UserParticipant) => void) {
    this.events_.off('joinedNewUser', handler);
  }
  onceJoinUser(handler: (trackOrAudioLevel?: UserParticipant) => void) {
    this.events_.once('joinedNewUser', handler);
  }
  private emitEvent(eventName: JitsiControllerEvents, trackOrAudioLevel?: TrackAndElement | number) {
    this.events_.emit(eventName, trackOrAudioLevel);
  }
  private emitEventUser(user: UserParticipant) {
    this.events_.emit('joinedNewUser', user);
  }
  doConnect(): Promise<JitsiController> {
    if (this.connectingPromise) {
      return this.connectingPromise;
    }
    this.status = 'connecting';
    this.connectingPromise = new Promise((resolv, reject) => {
      const connectionConfig = this.getConnectionConfig(this.conferenceId, this.mediaServerUrl);
      const connection = new JitsiMeetJS.JitsiConnection(null, null, connectionConfig) as JitsiConnection;
      const okHandler = () => {
        console.log('jitsi connection...... connected');
        this.status = 'connected';
        resolv(this);
      };
      const ngHandler = (reason: any) => {
        console.log('jitsi connection...... rejected', reason);
        connection.removeEventListener(JitsiMeetJS.events.connection.CONNECTION_FAILED, ngHandler);
        connection.disconnect();
        if (this.connection === connection) {
          this.connection = null;
          this.connectingPromise = null;
          setTimeout(() => {
            if (!this.connection) {
              this.doConnect().then(() => resolv(this));
            }
          }, 1000);
        }
      };
      connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED, okHandler);
      connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_FAILED, ngHandler);
      connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED, this.onConnectionDisconnect);
      this.connection = connection;
      connection.connect();
    });
    return this.connectingPromise;
  }
  async joinConrefence(displayName: string): Promise<void> {
    await this.doConnect();
    if (!this.connection) {
      return Promise.reject('no connection');
    }
    const conn = this.connection;
    return new Promise((resolv, reject) => {
      try {
        let conf = conn.initJitsiConference(this.conferenceId, {
          p2p: { enabled: false },
        });
        this.conference = conf;

        conf.setDisplayName(this.userId);
        conf.setLocalParticipantProperty('data-display-name', displayName);
        conf.addEventListener(JitsiMeetJS.events.conference.TRACK_ADDED, this.onRoomTrackAdded);
        conf.addEventListener(JitsiMeetJS.events.conference.TRACK_REMOVED, this.onRoomTrackRemoved);
        conf.addEventListener(JitsiMeetJS.events.conference.TRACK_MUTE_CHANGED, this.onRemoteTrackMuteChanged);
        conf.addEventListener(JitsiMeetJS.events.conference.USER_LEFT, this.onUserLeft);
        conf.addEventListener(JitsiMeetJS.events.conference.USER_JOINED, this.onUserJoined);
        conf.addEventListener(JitsiMeetJS.events.conference.CONFERENCE_FAILED, (reason: any) => {
          reject(reason);
        });
        const handler = () => {
          this.onUserJoined(this.conference?.myUserId() ?? '', null);
          resolv();
          conf.removeEventListener(JitsiMeetJS.events.conference.CONFERENCE_JOINED, handler);
        };
        conf.addEventListener(JitsiMeetJS.events.conference.CONFERENCE_JOINED, handler);
        conf.addEventListener(JitsiMeetJS.events.conference.CONFERENCE_ERROR, this.onConferenceError);
        conf.addEventListener(JitsiMeetJS.events.conference.TRACK_AUDIO_LEVEL_CHANGED, this.onAudioLevelChanged);
        conf.addEventListener(JitsiMeetJS.events.track.LOCAL_TRACK_STOPPED, () => {
          console.log('LOCAL_TRACK_STOPPED');
        });
        conf.addEventListener(
          JitsiMeetJS.events.conference.PARTICIPANT_PROPERTY_CHANGED,
          (user: JitsiParticipant, propertyKey: string, oldPropertyValue: string, propertyValue: string) => {
            console.log('participant property changed', user, propertyKey, oldPropertyValue, propertyValue);
          },
        );
        conf.join(); // パスワード付けるべきだね。。。
        this.status = 'opened';
      } catch (error) {
        console.error(error);
        this.status = 'error';
        reject(error);
      }
    });
  }

  private removeJitsiEventListeners() {
    if (this.conference) {
      this.conference.removeEventListener(JitsiMeetJS.events.conference.TRACK_ADDED, this.onRoomTrackAdded);
      this.conference.removeEventListener(JitsiMeetJS.events.conference.TRACK_REMOVED, this.onRoomTrackRemoved);
      this.conference.removeEventListener(
        JitsiMeetJS.events.conference.TRACK_MUTE_CHANGED,
        this.onRemoteTrackMuteChanged,
      );
      this.conference.removeEventListener(JitsiMeetJS.events.conference.USER_LEFT, this.onUserLeft);
      this.conference.removeEventListener(JitsiMeetJS.events.conference.USER_JOINED, this.onUserJoined);
      // this.conference.removeEventListener(JitsiMeetJS.events.conference.CONFERENCE_FAILED, this.onConferenceFailed);
      // this.conference.removeEventListener(JitsiMeetJS.events.conference.CONFERENCE_JOINED, this.onConferenceJoined);
      console.log('Removed events listeners of this.conference');
    }
  }

  async createAndAddAudioTrack(): Promise<JitsiTrack> {
    await this.removeAudioTrack();
    const ids = await this.validateDeviceIds();
    console.log('devices', ids, this.micId);
    const tracks: JitsiTrack[] = await JitsiMeetJS.createLocalTracks({
      devices: ['audio'],
      micDeviceId: ids.micId,
    });
    if (!this.conference) {
      tracks.forEach((t) => this.disposeTrack(t));
      throw new Error('fail create track. connference is removed');
    }
    if (tracks.length) {
      if (tracks.length !== 1) {
        console.warn(
          '想定外に複数のトラックが生成されました。',
          tracks.length,
          tracks.map((t) => t.getType()),
        );
      }
      // conference に track が残っていることがあるようなのでクリーンアップ
      const lt = this.conference.getLocalTracks();
      for (let t of lt) {
        if (t.getType() === 'audio') {
          await this.disposeTrack(t);
        }
      }
      let track = (this.localAudioTrack = tracks[0]);
      await this.conference.addTrack(track);
      return track;
    } else {
      throw new Error('fail create track');
    }
  }
  async createAndAddVideoTrack(): Promise<JitsiTrack> {
    if (this.isVideoOrDesktopTrackCreating) {
      console.warn('video track creating');
      return Promise.reject();
    }
    this.isVideoOrDesktopTrackCreating = true;

    if (this.localVideoTrack !== null || this.localDesktopTracks !== null) {
      console.warn('トラックがすでに存在します。', this.localVideoTrack, this.localDesktopTracks);
    }
    try {
      await this.doConnect();
      const ids = await this.validateDeviceIds();
      await Promise.all([this.removeVideoTrack(), this.removeDesktopTrack()]);
      let tracks: JitsiTrack[] = await JitsiMeetJS.createLocalTracks({
        devices: ['video'],
        cameraDeviceId: ids.cameraId,
        resolution: 200,
        constraints: {
          video: {
            aspectRatio: 1,
            frameRate: 2,
            height: {
              ideal: 110,
              max: 192,
              min: 64,
            },
          },
        },
      });
      if (!this.conference) {
        tracks.forEach((t) => this.disposeTrack(t));
        throw new Error('fail create track. connference is removed');
      }
      if (tracks.length) {
        let track = (this.localVideoTrack = tracks[0]);
        await this.conference.addTrack(track);
        return track;
      } else {
        throw new Error('fail create video track');
      }
    } finally {
      this.isVideoOrDesktopTrackCreating = false;
    }
  }
  async createAndAddDesktopTrack(): Promise<JitsiTrack[]> {
    if (this.isVideoOrDesktopTrackCreating) {
      console.warn('video track creating');
      return Promise.reject();
    }
    this.isVideoOrDesktopTrackCreating = true;
    try {
      await this.doConnect();
      await Promise.all([this.removeVideoTrack(), this.removeDesktopTrack()]);
      let tracks: JitsiTrack[] = await JitsiMeetJS.createLocalTracks({
        devices: ['desktop'],
      });
      const videoTrack = tracks.find((t) => t.getType() === 'video' && t.videoType === 'desktop');
      if (videoTrack) {
        // createLocalTracksの引数のconstraintで解像度を指定してもうまくいかないため、MediaStreamTrack.applyConstraintsで設定
        try {
          await videoTrack.track.applyConstraints(FULL_HD_CONSTRAINTS);
        } catch (error) {
          console.error(
            'Failed to start screen share. The following constraint failed to be met.→',
            (error as OverconstrainedError).constraint,
          );
        }
      } else {
        throw new Error('No desktop track found');
      }
      if (!this.conference) {
        tracks.forEach((t) => this.disposeTrack(t));
        throw new Error('fail create track. connference is removed');
      }
      if (tracks.length) {
        this.localDesktopTracks = tracks;

        for (const track of tracks) {
          await this.conference.addTrack(track);
          if (this.localDesktopTracks === tracks) {
            track.track.addEventListener('ended', () => {
              this.removeDesktopTrack();
            });
          }
        }
        return tracks;
      } else {
        throw new Error('fail create desktop track');
      }
    } finally {
      this.isVideoOrDesktopTrackCreating = false;
    }
  }
  removeAudioTrack(): Promise<any> {
    const track = this.localAudioTrack;
    this.localAudioTrack = null;
    if (track) {
      track.mute();
      console.log('disposing audio track', track?.getId());
    }
    return this.disposeTrack(track);
  }
  removeVideoTrack(): Promise<any> {
    let track = this.localVideoTrack;
    this.localVideoTrack = null;
    return this.disposeTrack(track);
  }
  removeDesktopTrack(): Promise<any> {
    if (!this.localDesktopTracks?.length) {
      return Promise.resolve();
    }
    let tracks = this.localDesktopTracks;
    this.localDesktopTracks = null;
    return Promise.all(tracks.map((t) => this.disposeTrack(t))).then(() => {
      this.emitEvent('desktopEnded');
    });
  }
  private async disposeTrack(track: JitsiTrack | null): Promise<any> {
    if (!track || track.disposed) {
      return Promise.resolve();
    }
    // this.conference?.removeTrack(track);
    try {
      return await track.dispose();
    } catch (e) {
      console.error(e);
      return Promise.resolve();
    }
  }
  private updateParticipants() {
    this.participants = Object.values(this.participantsMap);
  }
  /**
   * track の participantId を取得する。
   *
   * track.isLocal の場合 getParticipantId が取れないときがあるみたいなので実装。
   * @param track 対象のトラック
   * @returns 参加者ID
   */
  private getParticipantId(track: JitsiTrack): string {
    let p = track.getParticipantId();
    if (!p && track.isLocal()) {
      p = this.myParticipantId;
    }
    return p;
  }
  /**
   * トラック追加イベントハンドラー。
   * addEventListener 向けに アロー関数で実装.
   * @param track
   */
  private onRoomTrackAdded = (track: JitsiTrack) => {
    console.log('track added.', track);
    const te = {
      track,
      el: track.getType() === 'video' ? document.createElement('video') : document.createElement('audio'),
    };
    if (track.getType() === 'video') {
      // (te.el as HTMLVideoElement).disablePictureInPicture = true;
      (te.el as any).disablePictureInPicture = true; // コンパイルエラー対応
    }
    if (!track.isLocal() || track.getType() == 'video') {
      te.el.id = 'track-' + te.track.getId();
      te.el.volume = 0; // 後で更新されまで保留
      track.attach(te.el);
      console.log('this.speakerId', this.speakerId);
      if (track.getType() === 'audio' && this.speakerId) {
        track.setAudioOutput(this.speakerId);
      }
      if (!track.isLocal() && track.getType() === 'audio' && this.confId.startsWith('ss')) {
        te.el.volume = 1;
        te.el.play();
      }
      // applyVolume で呼ばれるのでここではしないように変更
      // te.el.play();
    }
    const pid = this.getParticipantId(track);
    const p = this.participantsMap[pid];
    if (p) {
      if (p.desktopTrack && track.getType() === 'video' && track.videoType === 'desktop') {
        p.tracks.splice(p.tracks.indexOf(p.desktopTrack), 1);
      }
      p.tracks.push(te);
      p.emitEvent('tracksUpdate', te);
    } else {
      // あり得ない？
      console.error('join していないユーザーの track が 追加されました。', pid, track);
    }
    this.tracks.push(te);
    this.emitEvent('tracksUpdate', te);
  };
  private onRoomTrackRemoved = (track: JitsiTrack) => {
    console.log('track removed.', track.getId());
    const id = track.getId();
    let i = this.tracks.findIndex((t) => t.track.getId() === id);
    if (i < 0) {
      console.log('already track removed.', track.getId(), track);
      return;
    }
    let tel = this.tracks.splice(i, 1)[0];
    if (tel?.el) {
      tel.el.pause();
      if (tel.el.parentElement) {
        tel.el.parentElement.removeChild(tel.el);
      }
    }
    const p = this.participantsMap[this.getParticipantId(track)];
    if (p) {
      const index = p.tracks.findIndex((t) => t.track.getId() === track.getId());
      if (index >= 0) {
        let te = p.tracks.splice(index, 1)[0];
        p.emitEvent('tracksUpdate', te);
        console.log('track removed. from paricipant', track);
      } else {
        console.warn('ユーザーのtrackとして設定されていないトラックが削除されました。', track);
      }
    } else if (!track.isLocal()) {
      // あり得ない？
      console.warn('join していないユーザーの track が 削除されました。', track);
    }
    this.emitEvent('tracksRemoved', tel);
  };
  private onRemoteTrackMuteChanged = (track: JitsiTrack) => {
    console.log('muted change', track, track?.videoType);
    let p = this.participantsMap[this.getParticipantId(track)];
    if (p) {
      const te = p.tracks.find((t) => t.track.getId() === track.getId());
      p.emitEvent('muteChanged', te);
    } else if (!track.isLocal()) {
      // あり得ない？
      console.warn('join していないユーザーの track が 削除されました。', track);
    }
    if (track.videoType === 'desktop') {
      // video に切り替わるっぽいけど考慮しないので一覧から削除する
      let index = this.tracks.findIndex((t) => t.track.getId() === track.getId());
      if (index >= 0) {
        const rtrack = this.tracks.splice(index, 1)[0];
        this.emitEvent('tracksRemoved', rtrack);
      }
    }
  };
  private onAudioLevelChanged = (participantId: string, level: number) => {
    let p = this.participantsMap[participantId];
    if (p) {
      p.emitEvent('audioLevelChanged', level);
    } else {
      // あり得ない？
      console.warn('join していないユーザーの audio level  が 変更されました。', participantId);
    }
  };
  private onConferenceError(e: any) {
    console.log('error occur', e);
  }
  private onUserJoined = (participantId: string, participant: JitsiParticipant | null): void => {
    console.log('user join to conference', participantId, participant, this.conference?.getParticipants());
    if (!participantId && !participant) {
      console.log('????', participantId, participant);
      return;
    }
    /* display name に ユーザーIDが設定されているので利用する。 */
    let userId = participant ? participant.getDisplayName() : this.userId;
    let userParticipant = this.participantsUserMap[userId];
    if (userParticipant && participant) {
      const currentParticipantId = userParticipant.participantId;
      if (currentParticipantId !== participantId) {
        userParticipant.emitUpdateParticipant(participant);
        this.participantsMap[participantId] = userParticipant;
        // 古い参照は削除
        delete this.participantsMap[currentParticipantId];
        this.updateParticipants();
      }
    } else if (userParticipant) {
      // 自分の場合 participant がないのでid だけで処理する
      if (participantId !== userParticipant.participantId) {
        const currentParticipantId = userParticipant.participantId;
        console.log('自分のparticipaint id が変わりました。', participantId, currentParticipantId);
        userParticipant.emitUpdateParticipantId(participantId);
        this.participantsMap[participantId] = userParticipant;
        // 古い参照は削除
        delete this.participantsMap[currentParticipantId];
        this.updateParticipants();
      }
    } else {
      userParticipant = new UserParticipant(participantId, userId, participant);
      this.participantsUserMap[userId] = userParticipant;
      this.participantsMap[participantId] = userParticipant;
      this.updateParticipants();
    }
    this.emitEventUser(userParticipant);
  };
  private onUserLeft = (participantId: any): void => {
    console.log('user left from conference', participantId, this.participantsMap);
    if (participantId && this.participantsMap && this.participantsMap[participantId]) {
      delete this.participantsMap[participantId];
      this.updateParticipants();
    }
  };

  private getConnectionConfig(roomName: string, serverName: string): JitsiConnectionConfig {
    const serverNameParsed = serverName.replace(/https?:\/\/(.*)\/?$/, '$1');
    const httpOrHttps = serverName.startsWith('https://') ? 'https' : 'http';

    const muc = `conference.${serverNameParsed}`;
    // const serviceUrl = `${httpOrHttps}://${serverNameParsed}/http-bind?room=${roomName}`;
    // `wss://${serverNameParsed}/xmpp-websocket?room=${roomName}`;
    // `//${serverNameParsed}/http-bind?room=${roomName}`;
    const serviceUrl = `wss://${serverNameParsed}/xmpp-websocket?room=${roomName}`; // `//${serverNameParsed}/http-bind?room=${roomName}`;
    const clientNode = `${httpOrHttps}://${serverNameParsed}`; // `https://${serverNameParsed}`; // 'http://jitsi.org/jitsimeet';

    const connectionConfig: JitsiConnectionConfig = {
      hosts: {
        domain: serverNameParsed,
        muc: muc,
      },
      serviceUrl: serviceUrl,
      clientNode: clientNode,
      // modules_enabled: ["presence_identity"]
    };
    console.log('Jitsi server connection configuration:', connectionConfig);

    return connectionConfig;
  }
  private onConnectionDisconnect = (reason: any) => {
    console.log('jitsi connection disconnected...', reason);
    if (reason) {
      this.disconnect();
    }
    this.events_.emit('disconnected');
  };
}
/**
 * https://github.com/jitsi/jitsi-meet/blob/master/react/features/stream-effects/noise-suppression/NoiseSuppressionEffect.ts
 * からコピー(Krisp 対応部分は除く)
 */

// AudioContext の生成・破棄を繰り返すと再生側の音声に影響が出ることがあるため、
// 本家と同様にシングルトンを suspend/resume で使い回す(close はしない)
let noiseSuppressionAudioContext: AudioContext | undefined;

export class NoiseSuppressionEffect implements JitsiAudioEffect {
  /**
   * Source that will be attached to the track affected by the effect.
   */
  private _audioSource?: MediaStreamAudioSourceNode;

  /**
   * Destination that will contain denoised audio from the audio worklet.
   */
  private _audioDestination?: MediaStreamAudioDestinationNode;

  /**
   * `AudioWorkletProcessor` associated node.
   */
  private _noiseSuppressorNode?: AudioWorkletNode;

  /**
   * Audio track extracted from the original MediaStream to which the effect is applied.
   */
  private _originalMediaTrack?: MediaStreamTrack;

  /**
   * Noise suppressed audio track extracted from the media destination node.
   */
  private _outputMediaTrack?: MediaStreamTrack;

  constructor(private readonly baseUrl: string) {}
  /**
   * Effect interface called by source JitsiLocalTrack.
   * Applies effect that uses a {@code NoiseSuppressor} service initialized with {@code RnnoiseProcessor}
   * for denoising.
   *
   * @param {MediaStream} audioStream - Audio stream which will be mixed with _mixAudio.
   * @returns {MediaStream} - MediaStream containing both audio tracks mixed together.
   */
  startEffect(audioStream: MediaStream): MediaStream {
    this._originalMediaTrack = audioStream.getAudioTracks()[0];

    if (!noiseSuppressionAudioContext) {
      // この AudioContext はマイク音声の加工専用でスピーカーへは何も出力しないが、
      // 通常の生成では動作中に物理出力デバイスを開いてしまい、環境によっては(例: 多チャンネルを
      // 広告する HDMI 出力)他の再生音声のチャンネルマッピングが壊れて片側からしか聞こえなくなる。
      // また、生成後に setSinkId で無音シンクへ切り替えるとデバイスの open→close 遷移が起き、
      // HDMI 側の再同期で他の再生音声が数秒無音になる。そのため生成時点から無音シンクを指定して
      // 物理出力デバイスに一切触れないようにする(Chrome 110+。未対応環境では従来どおりの生成になる)
      try {
        noiseSuppressionAudioContext = new (AudioContext as any)({ sinkId: { type: 'none' } }) as AudioContext;
      } catch (error) {
        console.warn('new AudioContext({sinkId:{type:"none"}}) failed', error);
        noiseSuppressionAudioContext = new AudioContext();
      }
      console.log('NS AudioContext sinkId:', (noiseSuppressionAudioContext as any).sinkId);
    }
    const audioContext = noiseSuppressionAudioContext;

    const audioSource = (this._audioSource = audioContext.createMediaStreamSource(audioStream));
    const audioDestination = (this._audioDestination = audioContext.createMediaStreamDestination());
    this._outputMediaTrack = audioDestination.stream.getAudioTracks()[0];

    // Connect the audio processing graph MediaStream -> AudioWorkletNode -> MediaStreamAudioDestinationNode
    this._initializeRnnoise(audioContext).then((filterNode) => {
      if (filterNode) {
        this._noiseSuppressorNode = filterNode;
        audioSource.connect(filterNode).connect(audioDestination);
      }
    });

    // Sync the effect track muted state with the original track state.
    this._outputMediaTrack.enabled = this._originalMediaTrack.enabled;

    // We enable the audio on the original track because mute/unmute action will only affect
    // the audio destination output track from this point on.
    this._originalMediaTrack.enabled = true;

    return audioDestination.stream;
  }

  /**
   * Checks if the JitsiLocalTrack supports this effect.
   *
   * @param {JitsiLocalTrack} sourceLocalTrack - Track to which the effect will be applied.
   * @returns {boolean} - Returns true if this effect can run on the specified track, false otherwise.
   */
  isEnabled(sourceLocalTrack: any): boolean {
    // JitsiLocalTracks needs to be an audio track.
    return sourceLocalTrack.isAudioTrack();
  }

  /**
   * Clean up resources acquired by noise suppressor and rnnoise processor.
   *
   * @returns {void}
   */
  stopEffect(): void {
    // Sync original track muted state with effect state before removing the effect.
    // ミュート中に effect を外した際、ミュート状態を元トラックへ引き継ぐ(これが無いとホットマイクになる)
    if (this._originalMediaTrack && this._outputMediaTrack) {
      this._originalMediaTrack.enabled = this._outputMediaTrack.enabled;
    }

    // Technically after this process the Audio Worklet along with it's resources should be garbage collected,
    // however on chrome there seems to be a problem as described here:
    // https://bugs.chromium.org/p/chromium/issues/detail?id=1298955
    this._noiseSuppressorNode?.port?.close();
    this._audioDestination?.disconnect();
    this._noiseSuppressorNode?.disconnect();
    this._audioSource?.disconnect();
    noiseSuppressionAudioContext?.suspend();

    console.log('call stop effect');
  }

  /**
   * Initializes the RNNoise audio worklet and creates the filter node.
   *
   * @returns {Promise<AudioWorkletNode | undefined>}
   */
  private async _initializeRnnoise(audioContext: AudioContext): Promise<AudioWorkletNode | undefined> {
    await audioContext.resume();

    const workletUrl = `${this.baseUrl}/libs/noise-suppressor-worklet.min.js`;
    console.log('worklet', workletUrl);
    try {
      await audioContext.audioWorklet.addModule(workletUrl);
    } catch (error) {
      console.error('Error while adding audio worklet module: ', error);
      return undefined;
    }

    // After the resolution of module loading, an AudioWorkletNode can be constructed.
    return new AudioWorkletNode(audioContext, 'NoiseSuppressorWorklet');
  }
}
